import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const ActivityVolumeVsRevenueChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, currencySettings, isLoading } = useBoardChartData();
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const chartData = useMemo(() => {
        if (!events || !finance?.dailyBreakdown) return [];

        const eventsByDate = new Map<string, number>();
        events.forEach((e: any) => {
            const date = e.date?.split('T')[0];
            if (date) eventsByDate.set(date, (eventsByDate.get(date) || 0) + 1);
        });

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        return sortedDates.map(dateStr => ({
            date: dateStr,
            dateLabel: format(parseISO(dateStr), 'MMM dd'),
            actions: eventsByDate.get(dateStr) || 0,
            revenue: finance.dailyBreakdown![dateStr].metrics.revenue || 0,
        }));
    }, [events, finance]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} vertical={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis dataKey="dateLabel" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis yAxisId="left" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={35} />
                        <YAxis yAxisId="right" orientation="right" {...CHART_STYLES.axis} tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={55} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        <Bar yAxisId="left" dataKey="actions" name={t('charts.actions', 'Actions') as string} fill={CHART_COLORS.chart.line5} opacity={0.5} radius={[3, 3, 0, 0]} maxBarSize={20} />
                        <Line yAxisId="right" type="monotone" dataKey="revenue" name={t('charts.revenue', 'Revenue') as string} stroke={CHART_COLORS.revenue} strokeWidth={2} dot={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
